/**
 * GoCommandAutocomplete - Page navigation autocomplete for @go command
 * 
 * Shows a searchable list of pages the user can navigate to.
 * Filters based on user's Cognito groups (permissions).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  navigationItems, 
  filterNavigationByGroups, 
  searchNavigation,
  type NavigationItem,
} from './Navigation/navigationConfig';
import { authService } from '../services/auth-service';
import type { PagePanelId } from '../types/panel-types';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

interface GoCommandAutocompleteProps {
  query: string;
  onSelect: (panelId: PagePanelId) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  userGroups?: string[];
}

export default function GoCommandAutocomplete({
  query,
  onSelect,
  onClose,
  position,
  userGroups: propUserGroups = [],
}: GoCommandAutocompleteProps) {
  const [localUserGroups, setLocalUserGroups] = useState<string[]>(propUserGroups);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Fetch user groups directly if not provided via props
  useEffect(() => {
    if (propUserGroups.length === 0) {
      authService.getCurrentUser().then(user => {
        if (user?.groups) {
          setLocalUserGroups(user.groups);
        }
      });
    } else {
      setLocalUserGroups(propUserGroups);
    }
  }, [propUserGroups]);
  
  // Filter navigation items based on user permissions and search query
  const accessibleItems = filterNavigationByGroups(navigationItems, localUserGroups);
  const filteredItems = searchNavigation(accessibleItems, query);
  
  // Group by category
  const pageItems = filteredItems.filter(i => i.category === 'pages');
  const settingsItems = filteredItems.filter(i => i.category === 'settings');
  const adminItems = filteredItems.filter(i => i.category === 'admin');
  
  // Flat list for keyboard navigation
  const allItems = useMemo(() => [...pageItems, ...settingsItems, ...adminItems], [pageItems, settingsItems, adminItems]);
  
  const handleKeySelect = useCallback((index: number) => {
    if (allItems[index]) {
      onSelect(allItems[index].panelId);
    }
  }, [allItems, onSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allItems.length,
    onSelect: handleKeySelect,
    onClose,
    resetDeps: [query],
  });
  
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  // Render a navigation item
  const renderItem = (item: NavigationItem, index: number) => {
    const isSelected = selectedIndex === index;
    
    return (
      <div
        key={item.panelId}
        data-index={index}
        onClick={() => onSelect(item.panelId)}
        className={`px-4 py-2.5 cursor-pointer flex items-center gap-3 transition-colors ${
          isSelected
            ? 'bg-info-light text-info'
            : 'text-foreground hover:bg-muted'
        }`}
      >
        <span className="text-lg w-6 text-center">{item.icon}</span>
        <div className="flex-1">
          <span className="font-medium">{item.label}</span>
          {item.keywords && item.keywords.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground">
              {item.keywords.slice(0, 2).join(', ')}
            </span>
          )}
        </div>
        {isSelected && (
          <span className="text-xs text-muted-foreground">↵</span>
        )}
      </div>
    );
  };
  
  let currentIndex = 0;
  
  return (
    <div
      ref={containerRef}
      className="command-autocomplete"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic positioning for autocomplete
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      {/* Header */}
      <div className="px-4 py-2 bg-secondary border-b border-border">
        <div className="text-xs font-semibold text-muted-foreground">
          🧭 Navigate to Page
        </div>
      </div>
      
      {/* Results */}
      <div className="max-h-[400px] overflow-y-auto">
        {allItems.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2">🔍</div>
            <div className="text-sm text-muted-foreground">
              No pages found for "{query}"
            </div>
          </div>
        ) : (
          <>
            {/* Pages */}
            {pageItems.length > 0 && (
              <>
                <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                  Pages
                </div>
                {pageItems.map(item => {
                  const idx = currentIndex++;
                  return renderItem(item, idx);
                })}
              </>
            )}
            
            {/* Settings */}
            {settingsItems.length > 0 && (
              <>
                <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                  Settings
                </div>
                {settingsItems.map(item => {
                  const idx = currentIndex++;
                  return renderItem(item, idx);
                })}
              </>
            )}
            
            {/* Admin */}
            {adminItems.length > 0 && (
              <>
                <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                  Admin
                </div>
                {adminItems.map(item => {
                  const idx = currentIndex++;
                  return renderItem(item, idx);
                })}
              </>
            )}
          </>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate • Enter Select • Esc Close</span>
          <span>{allItems.length} page{allItems.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

